/**
 * @module types/ambient
 * @description Tipos para el Modo Ambient (pantalla de monitoreo pasivo).
 *
 * El modo ambient se activa automáticamente cuando el usuario está inactivo durante
 * un período configurable. Muestra dashboards de solo lectura que rotan entre escenas:
 * - **compliance**: indicadores globales de cumplimiento ISO
 * - **network**: grafo de controles y sus relaciones
 * - **activity**: feed de actividad reciente anonimizado
 * - **institutional**: métricas institucionales de alto nivel
 *
 * ## Diseño de privacidad
 * `SafeAuditEntry` usa iniciales en lugar de nombres completos para que la pantalla
 * pueda mostrarse en espacios compartidos sin exponer información personal.
 *
 * @see AmbientModeContext para el contexto React
 * @see src/hooks/useIdleTimer.ts para el timer de inactividad
 */

// ─────────────────────────────────────────────────────────────────────────────
// Ambient / Monitoring Mode — Type Definitions
// ─────────────────────────────────────────────────────────────────────────────

export type SceneId = 'compliance' | 'network' | 'activity' | 'institutional';

export interface SceneConfig {
  id: SceneId;
  /** Duración de la escena antes de rotar (ms) */
  durationMs: number;
  label: string;
}

/** Orden y duración de rotación de escenas */
export const SCENE_ROTATION: SceneConfig[] = [
  { id: 'compliance',    durationMs: 25_000, label: 'Estado de Cumplimiento' },
  { id: 'network',       durationMs: 40_000, label: 'Red de Controles'       },
  { id: 'activity',      durationMs: 20_000, label: 'Actividad Reciente'     },
  { id: 'institutional', durationMs: 15_000, label: 'Estado Institucional'   },
];

// ─────────────────────────────────────────────────────────────────────────────
// Idle Timer
// ─────────────────────────────────────────────────────────────────────────────

export interface IdleTimerOptions {
  /** Tiempo sin actividad antes de activar modo ambient (ms) */
  timeoutMs: number;
  /** Eventos DOM que reinician el contador */
  events: (keyof DocumentEventMap)[];
  /** Callback cuando se supera el timeout */
  onIdle: () => void;
  /** Callback cuando el usuario vuelve a interactuar estando idle */
  onActive?: () => void;
  /** Si false, desconecta todos los listeners y pausa el timer */
  enabled: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

export interface AmbientModeContextType {
  isAmbient: boolean;
  activate: () => void;
  deactivate: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Entrada de auditoría anonimizada — segura para pantalla pública
// ─────────────────────────────────────────────────────────────────────────────

export interface SafeAuditEntry {
  id: string;
  action: string;
  actionType: string;
  /** Máximo 2 iniciales o "SIS" para acciones del sistema — NUNCA nombre completo */
  actorInitials: string;
  timestamp: string;
  criticality: 'low' | 'medium' | 'high' | 'critical';
  /** Cambio porcentual de cumplimiento, si existe */
  impactChange?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Network Scene
// ─────────────────────────────────────────────────────────────────────────────

export interface NetworkNode {
  id: string;
  label: string;
  status: 'completed' | 'in_progress' | 'not_started' | 'non_compliant';
  /** Posición en el SVG viewBox (0–800) */
  cx: number;
  cy: number;
  radius: number;
  completionRate: number;
}

export interface NetworkEdge {
  id: string;
  fromId: string;
  toId: string;
  /** Delay CSS para el pulso animado (ms) */
  pulseDelayMs: number;
  /** Coordenadas de los nodos para calcular el path */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface NetworkEvent {
  id: string;
  label: string;
  /** posición relativa (0–1) en el overlay */
  x: number;
  y: number;
  createdAt: number;
}
